import { FC, useEffect, useRef, useState } from 'react';

type LottieProps = {
    id: string;
    height?: number;
    width?: number;
    randomSpeed?: boolean;
};

const url = window.location.href.includes('localhost') ? '' : process.env.REACT_APP_FRONT_URL;

const cache: Record<string, { data?: string; error?: string; promise?: Promise<string> }> = {};

export function useLottieJson(url: string) {
    const [data, setData] = useState<string>(cache[url]?.data || '');
    const [error, setError] = useState<string>(cache[url]?.error || '');
    const [loading, setLoading] = useState<boolean>(!cache[url]?.data && !cache[url]?.error);

    useEffect(() => {
        if (!url) {
            return;
        }

        if (cache[url]?.data) {
            setData(cache[url].data as string);
            setLoading(false);

            return;
        }

        if (cache?.[url]?.promise) {
            setLoading(true);

            // @ts-ignore
            cache[url].promise
                .then((json) => {
                    setData(json as string);
                    setLoading(false);
                })
                .catch(() => {
                    setError('Error fetching lottie json');
                    setLoading(false);
                });

            return;
        }

        const promise = fetch(url)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Failed to fetch the file: ${res.status}`);
                }

                return res.text();
            })
            .then((json) => {
                cache[url].data = json;

                return json;
            })
            .catch((err) => {
                cache[url].error = err;
                // console.error(err);
                // throw err;
            });

        cache[url] = { promise: promise as Promise<string> };

        promise
            .then((json) => {
                setData(json as string);
                setLoading(false);
            })
            .catch((err) => {
                setError(err);
                setLoading(false);
            });
    }, [url]);

    return { data, error, loading };
}

const Lottie: FC<LottieProps> = ({ randomSpeed, id, width = 300, height = 300 }) => {
    const [loaded, setLoaded] = useState(false);
    const ref = useRef(null);
    const { data: animationData } = useLottieJson(`${url}/lottie/${id.replaceAll('/', '_')}.json`);

    const init = () => {
        const firstScriptTag = window.document.getElementsByTagName('SCRIPT')[0] as HTMLScriptElement;
        const createdScriptTag = window.document.createElement('SCRIPT') as HTMLScriptElement;

        createdScriptTag.type = 'text/javascript';
        createdScriptTag.id = 'lottie-script';
        createdScriptTag.async = true;
        createdScriptTag.src = 'https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js';
        createdScriptTag.onload = () => {
            setLoaded(true);
        };
        firstScriptTag.parentNode!.insertBefore(createdScriptTag, firstScriptTag);
    };

    useEffect(() => {
        const lottieScript = document.getElementById('lottie-script');

        if (!lottieScript) {
            init();
        } else {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        const player = ref.current;
        const handleComplete = () => {
            // @ts-ignore
            player?.stop();
            // @ts-ignore
            player?.play();

            setTimeout(() => {
                // @ts-ignore
                player?.pause();
                setTimeout(() => {
                    // @ts-ignore
                    player?.play();
                }, 4000 + Math.random() * 1000);
            }, 1000);
        };

        if (loaded && randomSpeed) {
            if (!player) {
                return;
            }

            // @ts-ignore
            player?.addEventListener('complete', handleComplete);
        }

        return () => {
            // @ts-ignore
            player?.removeEventListener('complete', handleComplete);
        };
    }, [loaded]);

    if (!loaded || !animationData) {
        return null;
    }

    return (
        // @ts-ignore
        <lottie-player
            ref={ref}
            autoplay
            loop={randomSpeed ? null : 1}
            background='transparent'
            height={height}
            speed={randomSpeed ? 0.8 + Math.random() * 0.2 : 1}
            src={animationData}
            width={width}
        />
    );
};

export default Lottie;
